import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for a `rating` answer (#21239): the stored number WITH its scale, e.g. `4 van 5`,
 * instead of the bare number the generic text display prints — "4" alone does not say out of how
 * many stars.
 *
 * The scale comes from `field.props['maxRating']` — not `props.max`: this field type never reads
 * that prop, and inventing a different key would make read and edit disagree about how many stars
 * there are.
 *
 * Where this mirrors `rating.component.ts#initializeStars` and where it deliberately does not:
 * - The FALLBACK is identical: `this.field.props?.maxRating || 5` (`rating.component.ts:113`),
 *   including its own quirk that a configured `0` collapses to the same default 5 as an
 *   unconfigured field (`0 || 5` is `5` in JS) — see the spec's dedicated case for that.
 * - The TYPE CHECK deliberately does not mirror `initializeStars`: that function hands its result
 *   straight to `Array.from({ length: maxRating }, …)`, whose `length` coercion accepts a numeric
 *   STRING (`'10'` silently renders ten stars) and even collapses a non-numeric truthy string
 *   (`'vijf'` → `NaN` → zero stars) without ever surfacing the config error. This display requires
 *   an actual `number`, so a string-typed `maxRating` falls through to "no scale found" here while
 *   edit mode would silently build (or silently mis-build) a star row from the same value. Nothing
 *   in the app writes a string `maxRating` today (no admin UI exposes this prop at all), so this
 *   divergence is theoretical, not observed — but it is real, so it is not claimed as mirrored.
 * - A negative `maxRating` also diverges: the `|| 5` check does not catch it (`-3` is truthy), so
 *   `initializeStars` would silently build a ZERO-star row via `Array.from({ length: -3 })`
 *   (negative lengths clamp to 0). This display shows the bare number instead of mirroring that
 *   collapse — an answer with no visible scale beats an answer that silently disappears, which is
 *   the one job a read row has.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-rating-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (scaledValue(); as scaledValue) {
      {{ scaledValue }}
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class RatingDisplayComponent extends ReadonlyDisplayBase {
  /**
   * `<value> van <scale>` when a scale can be resolved, the bare value as a string when it
   * cannot, or `null` for an empty/non-numeric answer — only `null` falls back to
   * `<dd-value-display>`, which renders the shared em dash for "no answer".
   */
  readonly scaledValue = computed(() => {
    const value = this.value();
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return null;
    }
    const scale = this.maxRating();
    return scale === null ? String(value) : `${value} van ${scale}`;
  });

  /**
   * Mirrors `rating.component.ts#initializeStars`'s own `this.field.props?.maxRating || 5`
   * fallback, then — unlike that function — rejects anything that does not resolve to a finite,
   * positive `number`. See the class doc for exactly which cases that type check deliberately
   * diverges on (a numeric-string `maxRating`, a negative one) and why.
   */
  readonly maxRating = computed<number | null>(() => {
    const configured = this.field().props?.['maxRating'] as number | undefined;
    const resolved = configured || 5;
    return typeof resolved === 'number' && Number.isFinite(resolved) && resolved > 0 ? resolved : null;
  });
}
